import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { authService } from '@/services/auth';

/**
 * Shown for the lifetime of an admin-triggered impersonation session (see
 * admin-content-plan's Impersonate action + AuthCallback.tsx, which is what
 * sets the 'impersonation_session' flag this reads). A normal login never
 * sets that flag, so this stays hidden for every real user session.
 */
export function ImpersonationBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isImpersonating, setIsImpersonating] = useState(
    () => localStorage.getItem('impersonation_session') === 'true'
  );
  const user = authService.getUser();

  useEffect(() => {
    const onStorage = () => setIsImpersonating(localStorage.getItem('impersonation_session') === 'true');
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (!isImpersonating) {
    return null;
  }

  const handleExit = () => {
    authService.clearAuth();
    localStorage.removeItem('impersonation_session');
    navigate('/login', { replace: true });
  };

  return (
    <div data-promo-hide className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 flex items-center justify-between sm:px-6 lg:px-8 z-50 relative">
      <div className="flex items-center gap-x-3 w-full justify-center">
        <ShieldExclamationIcon className="h-5 w-5 text-white/90" aria-hidden="true" />
        <p className="text-sm leading-6">
          <strong className="font-semibold">{t('impersonation.sessionActive')}</strong>
          {user?.email && (
            <>
              <span className="mx-2">&middot;</span>
              {t('impersonation.viewingAs', { email: user.email })}
            </>
          )}
        </p>
        <button
          onClick={handleExit}
          className="ml-2 flex-none rounded-full bg-white/20 px-3.5 py-1 text-sm font-semibold text-white shadow-sm hover:bg-white/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-colors cursor-pointer"
        >
          {t('impersonation.exit')}
        </button>
      </div>
    </div>
  );
}
